import React, { useEffect, useState, useRef } from 'react';
import { CTAButton } from './CTAButton';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
export function HeroSection() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    audio.currentTime = percent * duration;
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const progress = duration ? currentTime / duration * 100 : 0;
  return <section className="w-full relative min-h-[90vh] flex items-center justify-center px-4" style={{
    backgroundColor: '#0f172a'
  }} data-id="element-169">
      {/* Background image — optimized: smaller size, GPU-composited */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=60)',
      willChange: 'transform'
    }} aria-hidden="true" data-id="element-170" />
      <div className="absolute inset-0" style={{
      backgroundColor: 'rgba(15, 23, 42, 0.88)'
    }} aria-hidden="true" data-id="element-171" />

      <div className="max-w-3xl mx-auto text-center relative z-10 py-20" data-id="element-172">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6 uppercase" style={{
        color: '#f1f5f9'
      }} data-id="element-173">
          CÓMO GENERAR ENTRE{' '}
          <span style={{
          color: '#f59e0b'
        }} data-id="element-174">
            2 Y 6 MILLONES AL MES
          </span>{' '}
          CREANDO PÁGINAS WEB SIMPLES CON INTELIGENCIA ARTIFICIAL DESDE CASA
        </h1>

        <p className="text-lg md:text-xl mb-6" style={{
        color: '#94a3b8'
      }} data-id="element-175">
          Sin Experiencia en Diseño. Sin Código. Sin Conocimientos de Marketing.
        </p>

        <p className="text-xl md:text-2xl leading-relaxed mb-6" style={{
        color: '#f1f5f9'
      }} data-id="element-176">
          Descubre El Sistema Exacto Que Está Permitiendo a Principiantes Cobrar
          $200.000-$800.000 Por Proyecto — Creando Páginas Web Con IA En Menos
          de 1 Hora Desde La Comodidad de Su Casa
        </p>

        {/* Audio Player — preload="none" to avoid loading until user clicks play */}
        <div className="max-w-md mx-auto mb-10 rounded-lg px-4 py-3 flex items-center gap-3 border" style={{
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
        borderColor: '#334155'
      }} data-id="element-177">
          <audio ref={audioRef} src="https://ydgkmdmvhrniztrhtrxq.supabase.co/storage/v1/object/sign/audio%20receta/freepik__si-est_s-cansado-de-cursos-que-solo-ense_an-teor_a_-escucha-esto_-el-sistema-lpm-__1905.mp3?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80NDNkYTY1ZS1iYWM1LTQyYWQtODMzNS1iNWE4N2M0NDYxNmQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyByZWNldGEvZnJlZXBpa19fc2ktZXN0X3MtY2Fuc2Fkby1kZS1jdXJzb3MtcXVlLXNvbG8tZW5zZV9hbi10ZW9yX2FfLWVzY3VjaGEtZXN0b18tZWwtc2lzdGVtYS1scG0tX18xOTA1Lm1wMyIsImlhdCI6MTc3MjY2OTE5NiwiZXhwIjoxODA0MjA1MTk2fQ.YcM9puHPEUAY9YmhUVHfWbjbvc2otW8RlJYWGuHtyO8" preload="none" data-id="element-178" />

          <button onClick={togglePlay} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors" style={{
          backgroundColor: '#f59e0b'
        }} aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'} data-id="element-179">
            {isPlaying ? <PauseIcon className="h-5 w-5" style={{
            color: '#0f172a'
          }} data-id="element-180" /> : <PlayIcon className="h-5 w-5 ml-0.5" style={{
            color: '#0f172a'
          }} data-id="element-181" />}
          </button>

          <div className="flex-1 flex flex-col gap-1" data-id="element-182">
            <p className="text-xs font-bold text-left" style={{
            color: '#f59e0b'
          }} data-id="element-183">
              🎧 Escucha la Vista Previa
            </p>
            <div className="w-full h-2 rounded-full cursor-pointer" style={{
            backgroundColor: '#334155'
          }} onClick={handleSeek} data-id="element-184">
              <div className="h-full rounded-full transition-all duration-150" style={{
              backgroundColor: '#f59e0b',
              width: `${progress}%`
            }} data-id="element-185" />
            </div>
            <div className="flex justify-between" data-id="element-186">
              <span className="text-xs" style={{
              color: '#94a3b8'
            }} data-id="element-187">
                {formatTime(currentTime)}
              </span>
              <span className="text-xs" style={{
              color: '#94a3b8'
            }} data-id="element-188">
                {formatTime(duration)}
              </span>
            </div>
          </div>

          <button onClick={toggleMute} className="flex-shrink-0 p-1 cursor-pointer" aria-label={isMuted ? 'Activar sonido' : 'Silenciar'} data-id="element-189">
            {isMuted ? <VolumeXIcon className="h-4 w-4" style={{
            color: '#94a3b8'
          }} data-id="element-190" /> : <Volume2Icon className="h-4 w-4" style={{
            color: '#94a3b8'
          }} data-id="element-191" />}
          </button>
        </div>

        <CTAButton text="QUIERO ACCESO AL SISTEMA LPM™ POR SOLO $49.999" subtitle="✅ Guía PDF paso a paso · ✅ Descarga inmediata · ✅ Garantía de 30 días" scrollTo="hook-section" data-id="element-192" />
      </div>
    </section>;
}